import { keyframes } from "@emotion/react";

import { getThemeValue } from "@theme/components/ThemeUi";
import type { ThemeUIStyleObject } from "@theme/components/ThemeUi";

/**
 * Focus Styles
 *
 */
// animate focus ring to zoom out from the button
const focusAnimation = keyframes({
  from: { inset: 0, opacity: 0 },
  to: { inset: "-4px", opacity: 1 },
});

const focusAnimationOutline = keyframes({
  from: { inset: 0, opacity: 0 },
  to: { inset: "-3px", opacity: 1 },
});

const focusAnimationIcon = keyframes({
  from: { inset: 0, opacity: 0 },
  to: { inset: "-3px", opacity: 1 },
});

const focusAnimationDropdown = keyframes({
  from: { inset: 0, opacity: 0 },
  to: { inset: 0, opacity: 1 },
});

/**
 * Size Variants
 */
const smStyles = {
  py: "s300",
  px: "s600",
};

/**
 * Default Styling
 */
export default {
  position: "relative",
  cursor: "pointer",
  fontFamily: "textSans",
  fontSize: "fs19",
  fontWeight: "semibold",
  lineHeight: "lh200",
  letterSpacing: "tight100",
  textDecoration: "none",
  color: "textWhiteStatic",
  bg: "backgroundBrand",
  border: "none",
  borderRadius: "rounded",
  minWidth: 140,
  py: "s400",
  px: "s700",
  transition:
    "background-color .3s ease-in-out, color .3s ease-in-out, border-color .3s ease-in-out, opacity .3s ease-in-out",
  svg: {
    transition: "color .3s ease-in-out",
    color: "textWhiteStatic",
    width: "iconSizeBase",
    height: "iconSizeBase",
  },
  "&:hover": {
    bg: "backgroundInverse",
    color: "textInverse",
    svg: {
      color: "textInverse",
    },
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    bg: "backgroundBrand",
    color: "textWhiteStatic",
    svg: {
      color: "textWhiteStatic",
    },
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "rounded",
      animation: `${focusAnimation} 0.3s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderBrand",
    },
  },
  "&:active": {
    bg: "interactiveNeutralStrong",
  },
  "&:disabled": {
    cursor: "not-allowed",
    bg: "grey100",
    color: "textLevel3",
    "&:hover": {
      bg: "grey100",
      color: "interactiveNeutralHighlighted",
      svg: {
        color: "interactiveNeutralHighlighted",
      },
    },
  },
} as ThemeUIStyleObject;

/**
 * Primary variants
 *
 *  Duplicate of buttons.default, incase people want to use primary
 */
export const primary: ThemeUIStyleObject = {
  variant: "buttons.default",
};

export const primarySm: ThemeUIStyleObject = {
  variant: "buttons.default",
  ...smStyles,
};

/**
 * Primary Dark variants
 */
export const primaryDark: ThemeUIStyleObject = {
  variant: "buttons.default",
  "&:hover": {
    bg: "white",
    color: "black",
    svg: {
      color: "black",
    },
  },
  "&:active": {
    bg: "whiteAlpha700",
    color: "black",
    svg: {
      color: "black",
    },
  },
  "&:disabled": {
    bg: "grey100",
    color: "textLevel3",
    cursor: "not-allowed",
    "&:hover": {
      bg: "grey100",
      color: "interactiveNeutralHighlighted",
      svg: {
        color: "interactiveNeutralHighlighted",
      },
    },
  },
};

export const primaryDarkSm: ThemeUIStyleObject = {
  variant: "buttons.primaryDark",
  ...smStyles,
};

/**
 * Outlined variants
 */
export const outlined: ThemeUIStyleObject = {
  variant: "buttons.default",
  bg: "transparent",
  color: "textBrand",
  borderWidth: "thick",
  borderStyle: "solid",
  borderColor: "borderBrand",
  svg: {
    transition: "color .3s ease-in-out",
    color: "textBrand",
  },
  "&:hover": {
    bg: "transparent",
    color: "textPrimary",
    borderColor: "borderPrimary",
    svg: {
      color: "textPrimary",
    },
  },
  "&:active": {
    bg: "transparent",
    color: "textPrimary",
    borderColor: "borderPrimary",
    opacity: 0.7,
    svg: {
      color: "textPrimary",
    },
  },
  ".keyboard-user &:focus": {
    outline: "none",
    bg: "transparent",
    color: "textPrimary",
    borderColor: "borderPrimary",
    svg: {
      color: "textPrimary",
    },
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "rounded",
      animation: `${focusAnimationOutline} 0.3s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderPrimary",
    },
  },
  "&:disabled": {
    cursor: "not-allowed",
    bg: "grey100",
    color: "interactiveNeutralHighlighted",
    borderColor: "grey100",
    svg: {
      color: "interactiveNeutralHighlighted",
    },
    "&:hover": {
      bg: "grey100",
      color: "interactiveNeutralHighlighted",
      borderColor: "grey100",
      svg: {
        color: "interactiveNeutralHighlighted",
      },
    },
  },
  "&.loading": {
    bg: "transparent",
    color: "textPrimary",
    borderColor: "borderPrimary",
    svg: {
      color: "textPrimary",
    },
  },
};

export const outlinedSm: ThemeUIStyleObject = {
  variant: "buttons.outlined",
  ...smStyles,
};

/**
 * Outlined dark variants
 */
export const outlinedDark: ThemeUIStyleObject = {
  variant: "buttons.outlined",
  "&:hover": {
    borderColor: "white",
    color: "textWhiteStatic",
    svg: {
      color: "textWhiteStatic",
    },
  },
  "&:active": {
    color: "textWhiteStatic",
    borderColor: "white",
    opacity: 0.7,
    svg: {
      color: "white",
    },
  },
  ".keyboard-user &:focus": {
    color: "textWhiteStatic",
    borderColor: "white",
    svg: {
      color: "textWhiteStatic",
    },
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "rounded",
      animation: `${focusAnimationOutline} 0.3s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "white",
    },
  },
  "&:disabled": {
    bg: "grey100",
    color: "whiteAlpha500",
    borderColor: "grey100",
    svg: {
      color: "whiteAlpha500",
    },
    "&:hover": {
      bg: "grey100",
      color: "whiteAlpha500",
      borderColor: "grey100",
      svg: {
        color: "whiteAlpha500",
      },
    },
  },
  "&.loading": {
    color: "textWhiteStatic",
    borderColor: "white",
    svg: {
      color: "textWhiteStatic",
    },
  },
};

export const outlinedDarkSm: ThemeUIStyleObject = {
  variant: "buttons.outlinedDark",
  ...smStyles,
};

/**
 * Neutral variants
 */
export const neutral: ThemeUIStyleObject = {
  variant: "buttons.default",
  fontSize: "fs16",
  fontWeight: "medium",
  bg: "backgroundInverse",
  color: "textInverse",
  py: "s400",
  "&:hover": {
    bg: "grey500",
    color: "textWhiteStatic",
    svg: {
      color: "textWhiteStatic",
    },
  },
  "&:active": {
    bg: "grey500",
    color: "textWhiteStatic",
    opacity: 0.7,
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    bg: "backgroundInverse",
    color: "textInverse",
    svg: {
      color: "textInverse",
    },
    outline: "none",
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "rounded",
      animation: `${focusAnimation} 0.3s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "borderPrimary",
    },
  },
  "&:disabled": {
    cursor: "not-allowed",
    bg: "grey100",
    color: "interactiveNeutralHighlighted",
    svg: {
      color: "interactiveNeutralHighlighted",
    },
    "&:hover": {
      bg: "grey100",
      color: "interactiveNeutralHighlighted",
      svg: {
        color: "interactiveNeutralHighlighted",
      },
    },
  },
  "&.loading": {
    color: "textInverse",
    svg: {
      color: "textInverse",
    },
  },
};

export const neutralSm: ThemeUIStyleObject = {
  variant: "buttons.neutral",
  ...smStyles,
  fontSize: "fs14",
};

/**
 * Neutral variants
 */
export const neutralDark: ThemeUIStyleObject = {
  variant: "buttons.neutral",
  bg: "backgroundPrimary",
  color: "textPrimary",
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    bg: "backgroundPrimary",
    color: "textPrimary",
    svg: {
      color: "textPrimary",
    },
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "rounded",
      animation: `${focusAnimation} 0.3s ease-in-out forwards`,
      borderWidth: "thick",
      borderStyle: "solid",
      borderColor: "white",
    },
  },
  "&:disabled": {
    cursor: "not-allowed",
    bg: "grey100",
    color: "textWhiteStatic",
    svg: {
      color: "textWhiteStatic",
    },
    "&:hover": {
      bg: "grey100",
      color: "textWhiteStatic",
      svg: {
        color: "textWhiteStatic",
      },
    },
  },
  "&.loading": {
    color: "textPrimary",
    svg: {
      color: "textPrimary",
    },
  },
};

export const neutralDarkSm: ThemeUIStyleObject = {
  variant: "buttons.neutralDark",
  ...smStyles,
  fontSize: "fs14",
};

/**
 * Icon variants
 */

const iconSmStyles = {
  width: 28,
  height: 28,
  p: "s100",
  svg: {
    color: "textLevel2",
    width: "iconSizeSm",
    height: "iconSizeSm",
    transition: "color .3s ease-in-out",
  },
};

export const icon: ThemeUIStyleObject = {
  borderRadius: "full",
  cursor: "pointer",
  p: "s200",
  border: "none",
  lineHeight: "lh100",
  width: "iconSizeLg",
  height: "iconSizeLg",
  minWidth: "auto",
  bg: "transparent",
  "&, span": {
    fontSize: 0,
  },
  position: "relative",
  transition: "background-color .3s ease-in-out, color .3s ease-in-out, opacity .3s ease-in-out",
  svg: {
    color: "textLevel2",
    width: "iconSizeBase",
    height: "iconSizeBase",
    transition: "color .3s ease-in-out",
  },
  "&:hover": {
    bg: "interactiveNeutralFaded",
  },
  "&:active": {
    bg: "interactiveNeutralHighlighted",
    svg: { color: "textPrimary" },
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    bg: "backgroundBrand",
    svg: {
      color: "textWhiteStatic",
    },
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "rounded",
      animation: `${focusAnimationIcon} 0.3s ease-in-out forwards`,
      borderStyle: "solid",
      borderWidth: "thick",
      borderColor: "backgroundBrand",
    },
  },
  ":disabled": {
    cursor: "not-allowed",
    color: "textLevel2",
    opacity: 0.36,
  },
};

export const iconSm: ThemeUIStyleObject = {
  variant: "buttons.icon",
  ...iconSmStyles,
};

export const iconNeutral: ThemeUIStyleObject = {
  variant: "buttons.icon",
};

export const iconNeutralSm: ThemeUIStyleObject = {
  variant: "buttons.iconSm",
};

export const iconGold: ThemeUIStyleObject = {
  variant: "buttons.icon",
  svg: {
    color: "textBrand",
    width: "iconSizeBase",
    height: "iconSizeBase",
    transition: "color .3s ease-in-out",
  },
  "&:active": {
    bg: "interactiveNeutralHighlighted",
    svg: { color: "textBrand" },
  },
};

export const iconGoldSm: ThemeUIStyleObject = {
  variant: "buttons.iconGold",
  ...iconSmStyles,
  svg: {
    ...iconSmStyles.svg,
    color: "textBrand",
  },
};

export const iconDark: ThemeUIStyleObject = {
  variant: "buttons.icon",
  svg: {
    color: "grey200",
    width: "iconSizeBase",
    height: "iconSizeBase",
    transition: "color .3s ease-in-out",
  },
  "&:hover": {
    bg: "whiteAlpha300",
    svg: {
      color: "textWhiteStatic",
    },
  },
  "&:active": {
    bg: "whiteAlpha500",
    svg: { color: "textWhiteStatic" },
  },
};

export const iconDarkSm: ThemeUIStyleObject = {
  variant: "buttons.iconDark",
  ...iconSmStyles,
  svg: {
    ...iconSmStyles.svg,
    color: "grey200",
  },
};

export const iconSolid: ThemeUIStyleObject = {
  variant: "buttons.icon",
  bg: "backgroundBrand",
  svg: {
    color: "textWhiteStatic",
    width: "iconSizeBase",
    height: "iconSizeBase",
    transition: "color .3s ease-in-out",
  },
  "&:hover": {
    bg: "backgroundInverse",
    svg: {
      color: "textInverse",
    },
  },
  "&:active": {
    bg: "backgroundInverse",
    opacity: 0.64,
    svg: { color: "textInverse" },
  },
  ":disabled": {
    cursor: "not-allowed",
    color: "interactiveNeutralHighlighted",
    bg: "interactiveNeutralFaded",
  },
};

export const iconSolidSm: ThemeUIStyleObject = {
  variant: "buttons.iconSolid",
  ...iconSmStyles,
  svg: {
    ...iconSmStyles.svg,
    color: "textWhiteStatic",
  },
};

export const iconSolidDark: ThemeUIStyleObject = {
  variant: "buttons.iconSolid",
  svg: {
    color: "textWhiteStatic",
    width: "iconSizeBase",
    height: "iconSizeBase",
    transition: "color .3s ease-in-out",
  },
  "&:hover": {
    bg: "white",
    svg: {
      color: "grey600",
    },
  },
  "&:active": {
    bg: "white",
    opacity: 0.64,
    svg: { color: "grey600" },
  },
  ":disabled": {
    cursor: "not-allowed",
    bg: "whiteAlpha400",
    svg: {
      color: "whiteAlpha500",
    },
  },
};

export const iconSolidDarkSm: ThemeUIStyleObject = {
  variant: "buttons.iconSolidDark",
  ...iconSmStyles,
  svg: {
    ...iconSmStyles.svg,
    color: "textWhiteStatic",
  },
};

/*
 * Select Button Variants
 */

export const select: ThemeUIStyleObject = {
  cursor: "pointer",
  position: "relative",
  display: "flex",
  flex: ["0 0 auto", null, null, "1"],
  alignItems: "center",
  gap: "s300",
  font: "textSans",
  fontSize: "fs14",
  fontWeight: "medium",
  lineHeight: "lh400",
  textAlign: "left",
  color: "textLevel2",
  bg: "transparent",
  border: "0.5px solid",
  borderColor: "borderLevel2",
  borderRadius: "xs",
  width: 216,
  maxWidth: 216,
  px: "s300",
  py: "s400",
  svg: {
    color: "textBrand",
    width: "iconSizeMd",
    height: "auto",
  },
  span: {
    fontSize: "inherit",
    lineHeight: "inherit",
  },
  "&:hover": {
    bg: "transparent",
    color: "textPrimary",
    borderColor: "borderBrand",
  },
  "&:active": {
    bg: "interactiveNeutralDim",
    color: "textPrimary",
    borderColor: "borderBrand",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    bg: "transparent",
    color: "textPrimary",
    borderColor: "borderBrand",
    ":before": {
      content: "''",
      position: "absolute",
      animation: `${focusAnimationIcon} 0.3s ease-in-out forwards`,
      border: "2px solid",
      borderColor: "borderBrand",
      borderRadius: "xs",
    },
  },
};

/*
 * Play Button Variants
 */

export const play: ThemeUIStyleObject = {
  variant: "buttons.icon",
  bg: "white",
  width: [32, null, 40],
  height: [32, null, 40],
  p: "s200",
  boxShadow: "0px 4px 12px 0 rgba(0, 0, 0, 0.08)",
  transition: "box-shadow .3s ease-in-out",
  svg: {
    width: ["iconSizeSm", null, "iconSizeBase"],
    height: ["iconSizeSm", null, "iconSizeBase"],
    color: "black",
    transition: "color .3s ease-in-out",
  },
  "&:hover": {
    bg: "whiteAlpha800",
    boxShadow: "0px 8px 24px 0 rgba(0, 0, 0, 0.16)",
  },
  "&:active": {
    bg: "whiteAlpha800",
    boxShadow: "0px 8px 24px 0 rgba(0, 0, 0, 0.16)",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    bg: "white",
    boxShadow: "0px 8px 24px 0 rgba(0, 0, 0, 0.16)",
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "rounded",
      animation: `${focusAnimationIcon} 0.3s ease-in-out forwards`,
      border: "2px solid",
      borderColor: "goldPrimary",
    },
  },
};

export const playLg: ThemeUIStyleObject = {
  variant: "buttons.play",
  width: [64, null, 80],
  height: [64, null, 80],
  p: "s400",
  svg: {
    width: ["iconSizeMd", null, "iconSizeXl"],
    height: ["iconSizeMd", null, "iconSizeXl"],
    color: "black",
    transition: "color .3s ease-in-out",
  },
};

/*
 * Ghost Button Variants
 */
export const ghost: ThemeUIStyleObject = {
  bg: "transparent",
  border: 0,
  cursor: "pointer",
  color: "black",
  position: "relative",
  "&:hover, &:active": {
    bg: "transparent",
    border: 0,
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "none",
      m: "s100",
      animation: `${focusAnimationIcon} 0.3s ease-in-out forwards`,
      border: (t) => `2px solid ${getThemeValue(t, "colors.goldPrimary")}`,
    },
  },
};

/*
 * Link Button Variants
 */

const linkOverrides: ThemeUIStyleObject = {
  p: 0,
  bg: "transparent",
  span: { font: "inherit" },
};
// Need to overwrite some of the button defaults to match link styling
export const link: ThemeUIStyleObject = { variant: "links.default", ...linkOverrides };
export const linkDark: ThemeUIStyleObject = { variant: "links.linkDark", ...linkOverrides };
export const linkSm: ThemeUIStyleObject = { variant: "links.linkSm", ...linkOverrides };
export const linkDarkSm: ThemeUIStyleObject = { variant: "links.linkDarkSm", ...linkOverrides };
export const linkMedium: ThemeUIStyleObject = { variant: "links.linkMedium", ...linkOverrides };
export const linkMediumDark: ThemeUIStyleObject = {
  variant: "links.linkMediumDark",
  ...linkOverrides,
};
export const linkMediumSm: ThemeUIStyleObject = { variant: "links.linkMediumSm", ...linkOverrides };
export const linkMediumDarkSm: ThemeUIStyleObject = {
  variant: "links.linkMediumDarkSm",
  ...linkOverrides,
};
export const linkGold: ThemeUIStyleObject = { variant: "links.linkGold", ...linkOverrides };
export const linkGoldMedium: ThemeUIStyleObject = {
  variant: "links.linkGoldMedium",
  ...linkOverrides,
};

export const textLinkPrimary: ThemeUIStyleObject = {
  variant: "links.textLinkPrimary",
  ...linkOverrides,
};
export const textLinkPrimaryDark: ThemeUIStyleObject = {
  variant: "links.textLinkPrimaryDark",
  ...linkOverrides,
};
export const textLinkPrimaryMedium: ThemeUIStyleObject = {
  variant: "links.textLinkPrimaryMedium",
  ...linkOverrides,
};
export const textLinkPrimaryMediumDark: ThemeUIStyleObject = {
  variant: "links.textLinkPrimaryMediumDark",
  ...linkOverrides,
};
export const textLinkLevel2: ThemeUIStyleObject = {
  variant: "links.textLinkLevel2",
  ...linkOverrides,
};
export const textLinkLevel2Dark: ThemeUIStyleObject = {
  variant: "links.textLinkLevel2Dark",
  ...linkOverrides,
};
export const textLinkLevel2Medium: ThemeUIStyleObject = {
  variant: "links.textLinkLevel2Medium",
  ...linkOverrides,
};
export const textLinkLevel2MediumDark: ThemeUIStyleObject = {
  variant: "links.textLinkLevel2MediumDark",
  ...linkOverrides,
};

export const textLinkLevel3: ThemeUIStyleObject = {
  variant: "links.textLinkLevel3",
  ...linkOverrides,
};
export const textLinkLevel3Dark: ThemeUIStyleObject = {
  variant: "links.textLinkLevel3Dark",
  ...linkOverrides,
};
export const textLinkLevel3Medium: ThemeUIStyleObject = {
  variant: "links.textLinkLevel3Medium",
  ...linkOverrides,
};
export const textLinkLevel3MediumDark: ThemeUIStyleObject = {
  variant: "links.textLinkLevel3MediumDark",
  ...linkOverrides,
};

export const textLinkGold: ThemeUIStyleObject = { variant: "links.textLinkGold", ...linkOverrides };
export const textLinkGoldDark: ThemeUIStyleObject = {
  variant: "links.textLinkGoldDark",
  ...linkOverrides,
};
export const textLinkGoldMedium: ThemeUIStyleObject = {
  variant: "links.textLinkGoldMedium",
  ...linkOverrides,
};
export const textLinkGoldMediumDark: ThemeUIStyleObject = {
  variant: "links.textLinkGoldMediumDark",
  ...linkOverrides,
};

export const linkTrailingIcon: ThemeUIStyleObject = {
  variant: "links.trailingIcon",
  ...linkOverrides,
};

export const linkTrailingIconGold: ThemeUIStyleObject = {
  variant: "links.trailingIconGold",
  ...linkOverrides,
};

/**
 * Miscellaneous variants
 */

export const iconVideoPlayer: ThemeUIStyleObject = {
  variant: "buttons.icon",
  bg: "grey700",
  svg: {
    width: "iconSizeBase",
    height: "auto",
    color: "white",
    transition: "color .3s ease-in-out",
  },
  "&:hover": {
    bg: "grey500",
  },
};

export const iconScrollToTop: ThemeUIStyleObject = {
  variant: "buttons.iconDark",
  bg: "#2B2B2B",
  opacity: 0.7,
  width: [50, 60],
  height: [50, 60],
  borderRadius: "full",
  border: "1px solid #6a6a6a",
  svg: {
    color: "white",
    transition: "color .3s ease-in-out",
    transform: "rotate(270deg)",
    width: 20,
    height: 20,
  },
  "&:hover": {
    svg: {
      color: "grey200",
    },
  },
};

export const quantityIcon: ThemeUIStyleObject = {
  variant: "buttons.icon",
  borderRadius: "full",
  position: "relative",
  height: 28,
  width: 28,
  p: "s100",
  color: "inherit",
  transition: "background-color .3s ease-in-out, color .3s ease-in-out",
  svg: {
    width: "iconSizeBase",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate3D(-50%,-50%,0)",
    transition: "color .3s ease-in-out",
  },
  "&:hover": {
    bg: "grey400",
    svg: {
      color: "white",
    },
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    bg: "goldPrimary",
    svg: {
      color: "white",
    },
  },
};

export const halfPillButton: ThemeUIStyleObject = {
  position: "relative",
  width: 56,
  flex: "0 0 56px",
  height: 50,
  cursor: "pointer",
  borderTopRightRadius: "full",
  borderBottomRightRadius: "full",
  borderTopLeftRadius: "none",
  borderBottomLeftRadius: "none",
  outlineColor: "goldPrimary",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bg: "goldDark",
  "&:hover": {
    bg: "goldPrimary",
    color: "white",
  },
  "&:active": {
    bg: "grey300",
    color: "black",
  },
  "&:disabled": {
    opacity: 0.58,
    cursor: "not-allowed",
  },
};

export const dropdown: ThemeUIStyleObject = {
  display: "flex",
  height: "full",
  alignItems: "center",
  position: "relative",
  bg: "transparent",
  border: "none",
  fontSize: "fs14",
  fontFamily: "textSans",
  lineHeight: "lh100",
  color: "white",
  cursor: "pointer",
  transition: "background-color .3s ease-in-out, color .3s ease-in-out, opacity .3s ease-in-out",
  svg: {
    ml: "s200",
  },
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    color: "white",
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "none",
      animation: `${focusAnimationDropdown} 0.3s ease-in-out forwards`,
      border: (t) => `2px solid ${getThemeValue(t, "colors.goldPrimary")}`,
    },
  },
};

export const quantityButton: ThemeUIStyleObject = {
  bg: "white",
  color: "black",
  p: 0,
  alignItems: "center",
  justifyContent: "space-around",
  width: 116,
  height: 50,
  borderWidth: "default",
  borderColor: "grey200",
  borderRadius: "rounded",
  borderStyle: "solid",
};

export const phpScentButton: ThemeUIStyleObject = {
  variant: "buttons.link",
  width: 100,
  borderWidth: "thick",
  borderStyle: "solid",
  borderRadius: 0,
  transition: "border-color .3s ease",
  ":hover": {
    borderWidth: "thick",
    borderStyle: "solid",
  },
};

export const slickArrow: ThemeUIStyleObject = {
  variant: "buttons.iconSolid",
  position: "absolute",
  // Set positions inside each instance of slickArrow
  width: 60,
  height: 60,
};

export const galleryArrow: ThemeUIStyleObject = {
  variant: "buttons.slickArrow",
  bg: "grey700",
  width: 40,
  height: 40,
  opacity: 0.8,
  transition: "opacity 0.3s ease-in-out",
  svg: {
    color: "white",
    width: 20,
  },
  "&:hover": {
    opacity: 1,
  },
};

export const navbarItem: ThemeUIStyleObject = { variant: "links.navbarItem", ...linkOverrides };

export const phpSwatchButton: ThemeUIStyleObject = {
  minWidth: "36px",
  minHeight: "36px",
  fontSize: "fs12",
  borderRadius: "50%",
  cursor: "pointer",
};

export const thumbnail: ThemeUIStyleObject = {
  maxWidth: 64,
  width: "full",
  bg: "beige100",
  p: 0,
  borderWidth: "thick",
  cursor: "pointer",
  borderStyle: "solid",
  borderRadius: "none",
  transition: "border-color .2s ease-in-out",
  position: "relative",
  "&:focus": {
    outline: "none",
  },
  ".keyboard-user &:focus": {
    outline: "none",
    ":before": {
      content: "''",
      position: "absolute",
      borderRadius: "none",
      animation: `${focusAnimationIcon} 0.3s ease-in-out forwards`,
      border: (t) => `2px solid ${getThemeValue(t, "colors.goldLight")}`,
    },
  },
};

export const tooltip: ThemeUIStyleObject = {
  variant: "buttons.ghost",
  position: "absolute",
  top: 0,
  left: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  bg: "transparent",
  color: "grey300",
  lineHeight: 0,
};

export const tooltipPrimary: ThemeUIStyleObject = {
  ...tooltip,
  color: "goldPrimary",
};
